import { useCallback } from 'react';
import { analyticsHit, endSequentialKpi, kpiNames } from 'phoenix2/state/analytics/actions';
import {
    VIDEO_TYPE_INDY_TOPIC_KEY_SKILL_VIDEO,
    VIDEO_TYPE_SKILL,
    VIDEO_TYPE_TUTORIAL,
    VideoType,
} from 'phoenix2/state/video/video';
import { videoEnded, videoPaused, videoPlayed, videoStarted } from 'phoenix2/state/video/actions';
import { useDispatch } from 'react-redux';
import {
    assetFailedExplanation,
    assetLoadEnd,
    assetLoadFail,
    assetLoadStart,
    assetLoadSuccess,
} from 'phoenix2/state/assets/actions';
import { ASSET_TYPE } from 'phoenix2/state/assets/reducers';

/**
 * Converts video type to poster asset type
 * @param videoType the type of the video provided
 * @returns a string representing the asset type
 */
export const getPosterAssetType = (videoType: VideoType) => {
    switch (videoType) {
        case VIDEO_TYPE_TUTORIAL:
            return ASSET_TYPE.TUTORIAL_VIDEO_POSTER;
        case VIDEO_TYPE_INDY_TOPIC_KEY_SKILL_VIDEO:
            return ASSET_TYPE.INDY_TOPIC_KEY_SKILL_VIDEO_POSTER;
        default:
            return ASSET_TYPE.POSTER;
    }
};

/**
 * Converts video type to video asset type
 * @param videoType the type of the video provided
 * @returns a string representing the asset type
 */
export const getVideoAssetType = (videoType: VideoType) => {
    switch (videoType) {
        case VIDEO_TYPE_TUTORIAL:
            return ASSET_TYPE.TUTORIAL_VIDEO;
        case VIDEO_TYPE_INDY_TOPIC_KEY_SKILL_VIDEO:
            return ASSET_TYPE.INDY_TOPIC_KEY_SKILL_VIDEO;
        default:
            return ASSET_TYPE.VIDEO;
    }
};

/**
 * Custom hook to provide callbacks for a video
 * @param videoId the assetId for the video
 * @param videoType the type of the video provided
 * @param videoURL the URL for the video
 * @param posterURL the URL for the video's poster image
 * @param skillId the unique Id associated with a skill if a skill video
 * @param activityIndex the unique Id associated with the activity if a skill video
 * @param videoLoadFail callback function to notify the parent component of the failure
 * to load the video
 * @param additionalMetadata (optional) extra key-value pairs to put in the eventLabel of analytics
 * @returns an object with properties describing if the poster has been loaded,
 * if the poster has had an error loading, and the number of attempts to load the poster
 */
export const useVideoCallbacks = (
    videoId: string,
    videoType: VideoType,
    videoURL: string,
    posterURL: string,
    skillId?: string,
    activityIndex?: number,
    videoLoadFail?: () => void,
    additionalMetadata?: { [key: string]: string },
) => {
    const dispatch = useDispatch();
    if (additionalMetadata === undefined) {
        additionalMetadata = {};
    }

    const onStart = useCallback(
        (watchId: string) => {
            dispatch(
                analyticsHit('event', {
                    eventCategory: 'video',
                    eventAction: 'start',
                    eventLabel: {
                        videoType: videoType,
                        video_position: '' + 0,
                        skill_id: skillId,
                        video_id: videoId,
                        activity_index: String(activityIndex),
                        watch_id: watchId,
                        ...additionalMetadata,
                    },
                }),
            );
            if (videoType === VIDEO_TYPE_SKILL) {
                dispatch(videoStarted(watchId));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activityIndex, skillId, videoId, videoType],
    );

    const onPlay = useCallback(
        (watchId: string, currentTime: string, replay: boolean) => {
            if (!replay) {
                dispatch(
                    analyticsHit('event', {
                        eventCategory: 'video',
                        eventAction: 'play',
                        eventLabel: {
                            videoType: videoType,
                            video_position: '' + currentTime,
                            skill_id: skillId,
                            video_id: videoId,
                            activity_index: String(activityIndex),
                            watch_id: watchId,
                            ...additionalMetadata,
                        },
                    }),
                );
            }
            if (videoType === VIDEO_TYPE_SKILL) {
                dispatch(videoPlayed(watchId));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activityIndex, skillId, videoId, videoType],
    );

    const onPause = useCallback(
        (watchId: string, currentTime: string, reachedEnd: boolean) => {
            dispatch(
                analyticsHit('event', {
                    eventCategory: 'video',
                    eventAction: 'pause' + (reachedEnd ? ' at end' : ''),
                    eventLabel: {
                        videoType: videoType,
                        video_position: '' + currentTime,
                        skill_id: skillId,
                        video_id: videoId,
                        activity_index: String(activityIndex),
                        watch_id: watchId,
                        ...additionalMetadata,
                    },
                }),
            );
            if (videoType === VIDEO_TYPE_SKILL) {
                dispatch(videoPaused(watchId));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activityIndex, skillId, videoId, videoType],
    );

    const onSeeked = useCallback(
        (watchId: string, currentTime: string) => {
            dispatch(
                analyticsHit('event', {
                    eventCategory: 'video',
                    eventAction: 'jump',
                    eventLabel: {
                        videoType: videoType,
                        video_position: '' + currentTime,
                        skill_id: skillId,
                        video_id: videoId,
                        activity_index: String(activityIndex),
                        watch_id: watchId,
                        ...additionalMetadata,
                    },
                }),
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activityIndex, skillId, videoId, videoType],
    );

    const onFullscreen = useCallback(
        (watchId: string, currentTime: string) => {
            dispatch(
                analyticsHit('event', {
                    eventCategory: 'video',
                    eventAction: 'fullscreen',
                    eventLabel: {
                        videoType: videoType,
                        video_position: '' + currentTime,
                        skill_id: skillId,
                        video_id: videoId,
                        activity_index: String(activityIndex),
                        watch_id: watchId,
                        ...additionalMetadata,
                    },
                }),
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activityIndex, skillId, videoId, videoType],
    );

    const onReplay = useCallback(
        (watchId: string, currentTime: string) => {
            dispatch(
                analyticsHit('event', {
                    eventCategory: 'video',
                    eventAction: 'replay',
                    eventLabel: {
                        videoType: videoType,
                        video_position: '' + currentTime,
                        skill_id: skillId,
                        video_id: videoId,
                        activity_index: String(activityIndex),
                        watch_id: watchId,
                        ...additionalMetadata,
                    },
                }),
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activityIndex, skillId, videoId, videoType],
    );

    const onEnd = useCallback(
        (watchId: string, currentTime: string, error?: Error) => {
            const event = {
                eventCategory: 'video',
                eventAction: 'end',
                eventLabel: {
                    videoType: videoType,
                    video_position: '' + currentTime,
                    skill_id: skillId,
                    video_id: videoId,
                    activity_index: String(activityIndex),
                    watch_id: watchId,
                    ...additionalMetadata,
                },
            };
            if (error) {
                event.eventLabel['error'] = error;
            }
            dispatch(analyticsHit('event', event));
            if (videoType === VIDEO_TYPE_SKILL) {
                dispatch(videoEnded(watchId));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activityIndex, skillId, videoId, videoType],
    );

    const onVideoLoadStart = useCallback(
        (assetId: string) => {
            dispatch(assetLoadStart(assetId, getVideoAssetType(videoType)));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoType],
    );

    const onVideoLoadSuccess = useCallback(
        (assetId: string) => {
            dispatch(assetLoadSuccess(assetId, getVideoAssetType(videoType)));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoType],
    );

    const onVideoLoadFail = useCallback(
        (assetId: string, preload: boolean, error: MediaError) => {
            dispatch(
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                assetLoadFail(assetId, getVideoAssetType(videoType), videoURL, preload, error),
            );

            // Report error to parent component if required:
            if (videoLoadFail) {
                videoLoadFail();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoLoadFail, videoType],
    );

    const onVideoLoadEnd = useCallback(
        (assetId: string) => {
            dispatch(assetLoadEnd(assetId, getVideoAssetType(videoType)));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoType],
    );

    const onVideoCanPlay = useCallback(
        () => {
            dispatch(endSequentialKpi(kpiNames.VIDEO_PLAYABLE));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const onVideoErrorClicked = useCallback(
        () => {
            const assetType =
                videoType === VIDEO_TYPE_TUTORIAL ? ASSET_TYPE.TUTORIAL_VIDEO : ASSET_TYPE.VIDEO;
            dispatch(assetFailedExplanation(assetType));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoType],
    );

    // Poster load callbacks
    const onPosterLoadStart = useCallback(
        (assetId: string) => {
            dispatch(assetLoadStart(assetId, getPosterAssetType(videoType)));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoType],
    );

    const onPosterLoadSuccess = useCallback(
        (assetId: string) => {
            dispatch(assetLoadSuccess(assetId, getPosterAssetType(videoType)));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoType],
    );

    const onPosterLoadFail = useCallback(
        (assetId: string, preload: boolean, error: Error) => {
            dispatch(
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                assetLoadFail(assetId, getPosterAssetType(videoType), posterURL, preload, error),
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoType],
    );
    const onPosterLoadEnd = useCallback(
        (assetId: string) => {
            dispatch(assetLoadEnd(assetId, getPosterAssetType(videoType)));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoType],
    );

    return {
        onEnd,
        onFullscreen,
        onPause,
        onPlay,
        onPosterLoadEnd,
        onPosterLoadFail,
        onPosterLoadStart,
        onPosterLoadSuccess,
        onReplay,
        onSeeked,
        onStart,
        onVideoCanPlay,
        onVideoErrorClicked,
        onVideoLoadEnd,
        onVideoLoadFail,
        onVideoLoadStart,
        onVideoLoadSuccess,
    };
};
